/*
 * File: input-tree-select.ts
 * File Created: 2024-02-26 12:59:38
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-16 16:36:11
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { Component, Prop, Watch } from "vue-property-decorator";
import { BaseInputComponent } from "../core/lib";
import { PropInnateSync } from "@root/core/vue-props";
import CascadeSelect from "primevue/cascadeselect";
import { IQuery, IQueryDefault, ObjectHelper, StringHelper } from "@root/core";
import {
    IQuickSingleHierachyUnit,
    IQuickUnit,
    IQuickUnitGeneralizedSubTree,
    QuickHelper,
} from "@root/core/utilities/quick-helper";
import { IEntityPrimaryKey } from "@root/components/interfaces";
import { debounce } from "@root/core/decorators/debounce";
import { uiMetaRuleMatch } from "@root/core/tags/ui-meta";

export interface IInputTreeSelectData {
    "text-not-available": string;
}
const defaultData: IInputTreeSelectData = {
    "text-not-available": "w_InputTreeSelectNoAvailable",
};

@Component({
    components: { CascadeSelect },
})
export default class InputTreeSelect extends BaseInputComponent {
    @Prop({ type: String, default: "w_PleaseSelect" })
    declare placeholder: string;

    @PropInnateSync("value", { type: Object, default: undefined })
    innateValue!: IEntityPrimaryKey | undefined;
    get generatedValue() {
        if (this.innateValue == undefined) return this.innateValue;
        return "id" in this.innateValue ? this.innateValue.id : this.innateValue;
    }

    @Prop({ type: Array, required: true })
    quicks!: IQuickUnit[];

    @Prop({ type: Object, required: false })
    params: NonNullable<any> | undefined;

    /// params for the selected item fetch
    @Prop({ type: Object, required: false })
    tableParams: NonNullable<any> | undefined;

    /// convert the results to another
    /// normally used to filter / reduce the tree data
    @Prop({ type: Function, required: false })
    converter: any;

    @Prop({ type: Object, required: false })
    declare data: IInputTreeSelectData;
    get mergedData() {
        return {
            ...defaultData,
            ...this.data,
        };
    }

    // hold the data / loading / error
    query: IQuery = IQueryDefault();
    mapData: any = {};
    /// watcher
    @Watch("params", { immediate: true })
    onParamsUpdated(params: any, old: any) {
        if (params == null) return;
        if (ObjectHelper.equals(params, old)) return;
        this.reload();
    }

    @Watch("quicks", { immediate: true })
    onQuicksUpdated(quicks: IQuickUnit[] | null, old: IQuickUnit[] | null) {
        if (quicks == null) return;
        if (ObjectHelper.equals(quicks, old)) return;
        this.reload();
    }
    @debounce(500)
    reload() {
        /// handle quicks changes
        let hierachies = this.hierachies;
        if (hierachies == null) return;

        this.$nextTick(() => {
            if (hierachies == null) return;

            const quick = hierachies.quick;
            const path = hierachies.quick.path;

            let params = ObjectHelper.deepMerge(
                /// read params
                quick.paramsWhere?.call(quick),
                /// include children
                QuickHelper.includeChildren(hierachies),
                /// all data
                QuickHelper.MAX_PAGING,
                /// additional params
                this.params,
            );

            this.$server
                .useQuery(this.query, this.$server.R, path as any, params)
                .then((data: any) => {
                    this.mapData = {};
                    let results = data.results;
                    if (this.converter != null) results = this.converter(results);
                    let result = filterToMatchHierachy(
                        hierachies,
                        data.results,
                        this.mapData,
                    );
                    /// Translate the name
                    result.forEach((o) => (o.name = this.$_(o.name)));
                    this.options = result;
                });
        });
    }

    options: any = null;

    /// private helpers
    get uiMetaRuleMatch() {
        return uiMetaRuleMatch;
    }

    // three step update:
    // 1) if feed into value with { id: ... }, that's the initial value.
    // 2) when cascade tree selected, feed into value from hierachy (with no relations).
    // 3) when table being updated, feed full data into value.
    tableUpdated(value) {
        if ((value.results?.length ?? 0) > 0) {
            this.innateValue = value.results[0];
        }
    }

    get serverTable() {
        if (this.innateValue == null) return undefined;
        if (this.quicks.length === 0) return undefined;

        const quick = this.quicks[this.quicks.length - 1];
        return {
            interface: quick.infRead,
            server: ObjectHelper.deepMerge(QuickHelper.serverR(quick), {
                params: ObjectHelper.deepMerge({ where: { id: this.innateValue.id } }),
            }),
        };
    }

    get optionGroupChildren(): string[] {
        let hierachies = this.hierachies;
        let results: string[] = [];
        if (hierachies == null) return results;

        let children: IQuickUnitGeneralizedSubTree[] | null;
        while ((children = QuickHelper.getChildren(hierachies?.quick))) {
            const child = children[0];
            results.push(QuickHelper.makeChildrenProp(child.propertyName));
            hierachies = child;
        }
        return results;
    }

    get hierachies(): IQuickSingleHierachyUnit | null {
        //return QuickHelper.buildSingleHierachy(...this.quicks);
        return QuickHelper.buildHierachies(this.quicks, { mode: "equal" })[0]?.front;
    }

    /// handlers
    doCleanValue() {
        this.innateValue = undefined;
    }
}

function filterToMatchHierachy<T>(
    // hierachy: IQuickUnit[],
    hierachy: IQuickSingleHierachyUnit | null,
    data: T,
    mapData?: any,
): T | null;
function filterToMatchHierachy<T>(
    // hierachy: IQuickUnit[],
    hierachy: IQuickSingleHierachyUnit | null,
    data: T[],
    mapData?: any,
): T[] | null;
function filterToMatchHierachy(
    // hierachy: IQuickUnit[],
    hierachy: IQuickSingleHierachyUnit | null,
    data: any,
    mapData?: any,
): any {
    if (hierachy == null) return null;

    if (Array.isArray(data)) {
        /// array case
        const result = data
            .reduce((final, value) => {
                final.push(filterToMatchHierachy(hierachy, value, mapData));
                return final;
            }, [])
            .filter((o) => !!o);
        if (result.length > 0) return result;
        return null;
    }

    /// normal case
    let children = hierachy.quick.children?.();
    if (children == null) {
        /// last child of hierachy
        if (mapData != null && typeof mapData === "object") {
            mapData[data.id] = data;
        }
        return data;
    }

    let child = QuickHelper.generalize(children[0]);
    const propName = QuickHelper.makeChildrenProp(child.propertyName);
    const next = data[propName];
    if (next == null) return null;

    const result = filterToMatchHierachy(child, next, mapData);
    if (result == null) return null;
    data[propName] = result;
    return data;
}
